// Thank-you page after submitting quotation request (plan: onboarding post-envío).
// Shows "Muchas gracias", message that we're working on it, and CTAs: crear cuenta, iniciar sesión, ver cotizaciones (if logged in), volver al catálogo.

import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { postWorkerApi } from '@/lib/worker-api';
import { useClientLead } from '@/hooks/use-store';
import { StoreBreadcrumbs } from '@/components/store/StoreBreadcrumbs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle2, LogIn, History, ShoppingBag, Mail } from 'lucide-react';

type LocationState = { email?: string } | null;

const StoreCartRequestSuccess = () => {
  const { state } = useLocation() as { state?: LocationState };
  const { data: clientLead } = useClientLead();
  const { toast } = useToast();
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  const email = state?.email?.trim();
  const isLoggedIn = !!clientLead;

  const handleSendVerificationLink = async () => {
    if (!email) return;
    setSendingLink(true);
    try {
      const res = await postWorkerApi('/api/send-verification-code', { email });
      if (res.ok) {
        setLinkSent(true);
        toast({
          title: 'Correo enviado',
          description: 'Revisa tu bandeja de entrada. Te hemos enviado un enlace para crear tu cuenta.',
          variant: 'success',
        });
      } else {
        toast({
          title: 'Error',
          description: res.error ?? 'No se pudo enviar el correo',
          variant: 'destructive',
        });
      }
    } finally {
      setSendingLink(false);
    }
  };

  const breadcrumbs = [
    { label: 'Store', path: '/store' },
    { label: 'Orden', path: '/store/cart' },
    { label: 'Solicitud enviada' },
  ];

  return (
    <div className="space-y-6">
      <StoreBreadcrumbs segments={breadcrumbs} />
      <Card className="border-zinc-200 max-w-xl mx-auto">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <CheckCircle2 className="h-8 w-8" strokeWidth={2} />
            </div>
          </div>
          <CardTitle className="text-2xl text-zinc-900">Muchas gracias</CardTitle>
          <CardDescription className="text-base text-zinc-600">
            Hemos recibido tu solicitud de cotización. Estamos trabajando en ello y te contactaremos en breve.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 pt-2">
          {isLoggedIn ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <Button asChild className="flex-1 gap-2">
                <Link to="/store/quotations">
                  <History className="h-4 w-4" />
                  Ver mis cotizaciones
                </Link>
              </Button>
              <Button asChild variant="outline" className="flex-1 gap-2">
                <Link to="/store">
                  <ShoppingBag className="h-4 w-4" />
                  Volver al catálogo
                </Link>
              </Button>
            </div>
          ) : (
            <>
              <p className="text-sm text-zinc-600 text-center">
                ¿Quieres crear una cuenta para ver el estado de tu cotización y tu historial?
              </p>
              {email && !linkSent ? (
                <Button
                  className="w-full gap-2"
                  disabled={sendingLink}
                  onClick={handleSendVerificationLink}
                >
                  <Mail className="h-4 w-4" />
                  {sendingLink ? 'Enviando...' : 'Enviar enlace para crear cuenta'}
                </Button>
              ) : email && linkSent ? (
                <p className="text-sm text-emerald-700 text-center font-medium">
                  Revisa tu correo ({email}). Te hemos enviado un enlace para verificar tu cuenta y crear tu contraseña.
                </p>
              ) : null}
              <div className="flex flex-col sm:flex-row gap-2">
                <Button asChild variant="outline" className="flex-1 gap-2">
                  <Link to="/store/login">
                    <LogIn className="h-4 w-4" />
                    Iniciar sesión
                  </Link>
                </Button>
                <Button asChild variant="outline" className="flex-1 gap-2">
                  <Link to="/store">
                    <ShoppingBag className="h-4 w-4" />
                    Volver al catálogo
                  </Link>
                </Button>
              </div>
              {!email && (
                <p className="text-xs text-zinc-500 text-center">
                  ¿Primera vez? Inicia sesión no está disponible hasta que verifiques tu cuenta; te enviamos un enlace por correo cuando solicitas una cotización.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StoreCartRequestSuccess;
